// Dateien eines Leads (API.md, Abschnitt „Dateien“): Liste aus den Aktivitäten, Abruf über den Server, Hochladen.
// Eigene Datei, damit api/index.ts unberührt bleibt. Die Dateiadresse zeigt immer auf das CRM (nie auf Close).
import { ApiFehler, http } from "./client";
import type { FehlerAntwort } from "./types";

export interface LeadDatei {
  /** `<activity_id>~<Nummer>` */
  id: string;
  name: string;
  size: number | null;
  content_type: string | null;
  /** PDF, Bild, Word, Excel, PowerPoint, Audio, Video, Archiv, Text oder Datei */
  art: string;
  date: string | null;
  quelle: "email" | "notiz" | "sms";
  richtung: string | null;
  titel: string | null;
  activity_id: string;
  user_id: string | null;
  user_name: string | null;
  eingebettet: boolean;
  /** false: nur im CRM gesendet, es gibt keinen Abruf in Close */
  herunterladbar: boolean;
}

export interface DateienListe {
  data: LeadDatei[];
  total: number;
  /** größte erlaubte Datei in Byte */
  max_bytes: number;
}

export const dateienApi = {
  liste: (leadId: string) => http.get<DateienListe>(`/api/leads/${encodeURIComponent(leadId)}/dateien`),

  /** Adresse zum Öffnen/Laden (Cookie-Anmeldung genügt). herunterladen: immer speichern statt anzeigen. */
  url: (leadId: string, dateiId: string, herunterladen = false) =>
    `/api/leads/${encodeURIComponent(leadId)}/dateien/${encodeURIComponent(dateiId)}${herunterladen ? "?herunterladen=true" : ""}`,

  /** Rohe Datei im Body; der Server legt sie bei Close ab und trägt eine Notiz mit Anhang am Lead ein. */
  async hochladen(leadId: string, datei: File): Promise<{ datei: LeadDatei | null }> {
    let antwort: Response;
    try {
      antwort = await fetch(`/api/leads/${encodeURIComponent(leadId)}/dateien`, {
        method: "POST",
        credentials: "same-origin",
        headers: {
          Accept: "application/json",
          "Content-Type": datei.type || "application/octet-stream",
          "X-Dateiname": encodeURIComponent(datei.name),
        },
        body: datei,
      });
    } catch {
      throw new ApiFehler(0, "keine_verbindung", "Keine Verbindung zum CRM-Server.");
    }
    const text = await antwort.text();
    let daten: unknown;
    try {
      daten = text ? JSON.parse(text) : undefined;
    } catch {
      daten = undefined;
    }
    if (!antwort.ok) {
      const fehler = (daten as FehlerAntwort | undefined)?.error;
      if (antwort.status === 413 && !fehler) throw new ApiFehler(413, "zu_gross", "Die Datei ist zu groß.");
      throw new ApiFehler(antwort.status, fehler?.code ?? "fehler", fehler?.message ?? `Hochladen fehlgeschlagen (${antwort.status}).`, fehler?.details ?? {});
    }
    return daten as { datei: LeadDatei | null };
  },
};
